import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from './config';
import { getServerErrorKind } from 'src/services/serverErrorNotifier';
// eslint-disable-next-line camelcase
import jwt_decode from 'jwt-decode';



const initialState = {
  accessToken: localStorage.getItem('accessToken') || '',
  profile: {},
  loading: false,
  isSuccess: false,
  errors: [],
  role:''
};


export const signin = createAsyncThunk('auth/signin', async (body, { rejectWithValue }) => {
  try {
    const params = { email: body.email, password: body.password };
const response = await api.post('/auth/signin', params);  // URL relative

    // Seul accessToken va en localStorage : refreshToken arrive désormais aussi
    // via un cookie httpOnly posé par le serveur (voir config.js), inaccessible
    // au JS et donc invulnérable à un vol par script (XSS).
    localStorage.setItem('accessToken', response.data.accessToken);

    return response.data;
  } catch (error) {
    // Internet coupé ou serveur en panne (5xx, ex. 504 renvoyé en HTML par nginx) :
    // la réponse n'a pas de message exploitable, on renvoie un code traduit
    // par la page de connexion (voir LOGIN_ERROR_KEYS dans Hero).
    var kind = getServerErrorKind(error);
    if (kind) {
      var code = kind === 'network' && navigator.onLine === false ? 'offline' : kind;
      return rejectWithValue({ message: 'server_error_' + code });
    }
    return rejectWithValue(error.response.data);
  }
});

// Best effort : même si l'appel échoue (ex: access token déjà expiré), l'état
// local est nettoyé dans tous les cas via logout.fulfilled. Le cookie httpOnly
// expirera de lui-même, et refresh_token est de toute façon invalidé en base
// dès que l'appel réussit (voir UserService.logout côté backend).
export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await api.post('/auth/logout');
  } catch (error) {
    // ignoré volontairement : le nettoyage local doit avoir lieu quoi qu'il arrive
  }
});

export const getProfile = createAsyncThunk('user/profile', async (__, { rejectWithValue, dispatch }) => {
  try {
    // Cache-bust: this GET carries an ETag but no Cache-Control, so the browser can serve
    // a stale cached response (e.g. an old profile picture id) instead of hitting the network.
    const response = await api.get(`/user/profile`, {
      params: { _: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
    });
    return response.data;
  } catch (error) {
    if (!error.response) {
      throw error;
    }
    if (error.response.status === 401) {
      dispatch(logout());
    }
    return rejectWithValue(error.response.data);
  }
});


export const updateProfile = createAsyncThunk('user/updateprofile', async (body, { rejectWithValue, dispatch }) => {
  try {
    const params = { email: body.email, phone: body.phone, firstname:body.firstname , lastname:body.lastname };
    const response = await api.put(`/user/profile`,params);
    return response.data;
  } catch (error) {
    if (!error.response) {
      throw error;
    }
    if (error.response.status === 401) {
      dispatch(logout());
    }
    return rejectWithValue(error.response.data);
  }
});

export const uploadProfilePicture = createAsyncThunk(
  'user/uploadProfilePicture',
  async (file, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/user/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePassword = createAsyncThunk('user/updatePassword', async (body, { rejectWithValue, dispatch }) => {
  try {
    // old_password est obligatoire côté backend : une session seule ne suffit pas
    // pour changer le mot de passe.
    const params = { old_password: body.old_password, password: body.password, passwordConfirm: body.passwordConfirm };
    const response = await api.put(`/user/change-password`,params);
    return response.data;
  } catch (error) {
    if (!error.response) {
      throw error;
    }
    if (error.response.status === 401) {
      dispatch(logout());
    }
    return rejectWithValue(error.response.data);
  }
});

// user 
const authSlice = createSlice({
  name: 'superadmin',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, (state) => {
        state.accessToken = null;
        state.errors = [];
        state.profile = {};
        localStorage.clear();
      })
      .addCase(signin.pending, (state) => {
        state.loading = true;
      })
      .addCase(signin.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        if (!payload?.accessToken || !payload?.refreshToken) {
          state.isSuccess = false;
          state.errors = ['AccessToken and/or refreshToken not found'];
        }
        else {
          state.accessToken = payload.accessToken;
          const decodedToken = jwt_decode(payload.accessToken);
          state.email = decodedToken.email;
          state.role = decodedToken.role;

          // Un compte assistant (SUPER_ADMIN_ASSISTANT) a le droit de se connecter à cette
          // plateforme, avec un accès borné géré ensuite via profile.permissions — ce n'est
          // pas ici qu'on restreint ce qu'il peut voir/faire. Cette vérification doit rester
          // dans ce bloc : sinon elle s'exécute même quand les tokens sont absents et écrase
          // le message d'erreur "tokens manquants" ci-dessus par celui-ci.
          if (state.role !== 'SUPER_ADMIN' && state.role !== 'SUPER_ADMIN_ASSISTANT') {
            state.isSuccess = false;
            state.errors = ['Without authorization, you do not have super admin privileges.'];
          }
        }

        state.loading = false;
      })
      .addCase(signin.rejected, (state, { payload }) => {
        // if (payload.statusCode === 400) {
        state.errors = payload?.message;
        // }
        state.isSuccess = false;
        state.loading = true; // Should this be false? Original was true.
      })
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProfile.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.profile = payload;
        state.loading = false;
      })
      .addCase(getProfile.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfile.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.profile = payload;
        state.loading = false;
      })
      .addCase(updateProfile.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(uploadProfilePicture.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadProfilePicture.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.profile = payload;
        state.loading = false;
      })
      .addCase(uploadProfilePicture.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePassword.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(updatePassword.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      });
  }
});

export default authSlice.reducer;
